import { Component, OnInit } from '@angular/core';
import { CompanySettingsService } from "./company-settings.service";
import { CVLocalizationService } from '../../reusables/services/cv-localization.service';
import { CompanySettingsValues, CompanySettingsType, FieldGroup, SSOSettingsValues, DEFAULT_COMPANY_TOOLTIP_TEXT } from "./company-settings.models";
import { cloneDeep } from 'lodash';
import { SsoSettings } from 'src/app/admin/company-settings/company-edit-sso/company-edit-sso.model';

@Component({
    selector: 'cv-company-settings',
    templateUrl: './company-settings.component.html',
    styleUrls: ['./company-settings.component.scss']
})
export class CompanySettingsComponent implements OnInit {
    // Map<fieldGroups, Map<fieldName, CompanySettingsValues>;
    settings: Map<string, Map<string, CompanySettingsValues>>;
    prevSettings: CompanySettingsValues[];
    loading: boolean = true;
    hasChange: boolean = false;
    saving: boolean = false;
    savingSSO: boolean = false;
    saveSuccess: boolean = false;

    public editing: boolean = false;
    public fieldGroups: FieldGroup[] = [];
    public prevSSOSettingsValues: SsoSettings = null;
    public ssoSettingsValues: SsoSettings = null;
    public ssoErrors: string[] = [];
    public locDic: Map<string, string>;

    constructor(
        private CompanySettingsService: CompanySettingsService,
        localization: CVLocalizationService,
    ) {
        localization.GetSection("Company_Settings").then(res => {
            this.locDic = res;
            //try to localize the section headers
            if (res.get("General") != null)
                this.fieldGroups.push({ "GroupName": "General", "DisplayName": res.get("General") });
            else
                this.fieldGroups.push({ "GroupName": "General", "DisplayName": "General" });

            if (res.get("Customization") != null)
                this.fieldGroups.push({ "GroupName": "Customization", "DisplayName": res.get("Customization") });
            else
                this.fieldGroups.push({ "GroupName": "Customization", "DisplayName": "Customization" });

            if (res.get("DataFilter") != null)
                this.fieldGroups.push({ "GroupName": "Data Filter", "DisplayName": res.get("DataFilter") });
            else
                this.fieldGroups.push({ "GroupName": "Data Filter", "DisplayName": "Data Filter" });

            if (res.get("Gamification") != null)
                this.fieldGroups.push({ "GroupName": "Gamification", "DisplayName": res.get("Gamification") });
            else
                this.fieldGroups.push({ "GroupName": "Gamification", "DisplayName": "Gamification" });
        });
    }

    async ngOnInit() {
        this.loading = true;

        this.prevSettings = await this.CompanySettingsService.GetCompanySettings();

        this.prevSSOSettingsValues = await this.CompanySettingsService.GetSSOSettings();
        this.ssoSettingsValues = angular.copy(this.prevSSOSettingsValues);

        this.settings = new Map();
        this.settings.set("General", new Map());
        this.settings.set("Customization", new Map());
        this.settings.set("Data Filter", new Map());
        this.settings.set("Gamification", new Map());
        for (var i = 0; i < this.prevSettings.length; i++) {
            if (this.prevSettings[i].FieldGroup == null)
                this.settings.get("General").set(this.prevSettings[i].FieldName, JSON.parse(JSON.stringify(this.prevSettings[i])));
            else
                this.settings.get(this.prevSettings[i].FieldGroup).set(this.prevSettings[i].FieldName, JSON.parse(JSON.stringify(this.prevSettings[i])));
        }

        this.loading = false;
    }

    public async save() {
        if (this.savingSSO || this.saving)
            return;

        if (this.ssoErrors.length == 0) {
            //if pass then await call
            this.savingSSO = true;
            try {
                let savedSSO = await this.CompanySettingsService.SetCompanySSOSettings(this.ssoSettingsValues);
                if (savedSSO != null) {
                    this.saveSuccess = true;

                    this.prevSSOSettingsValues = cloneDeep(savedSSO);
                    this.ssoSettingsValues = cloneDeep(savedSSO);

                    if (this.ssoSettingsValues.SSOClientSecret != "") {
                        this.ssoSettingsValues.SSOClientSecret = "**********";
                        this.prevSSOSettingsValues.SSOClientSecret = "**********";
                    }
                    else
                        this.prevSSOSettingsValues.SSOClientSecret = "";
                }
                else
                    this.saveSuccess = false;
            }
            catch (e) {
                this.saveSuccess = false;
            }
            finally {
                this.savingSSO = false;
                this.onChange();
            }
        }
        else {
            this.savingSSO = false;
            this.saveSuccess = false;
        }

        let changes: CompanySettingsValues[] = [];
        for (var i = 0; i < this.prevSettings.length; i++) {
            const fieldName = this.prevSettings[i].FieldName;
            const fieldGroup = this.prevSettings[i].FieldGroup != null ? this.prevSettings[i].FieldGroup : "General";
            const fieldType = this.prevSettings[i].FieldType;

            if (fieldType == CompanySettingsType.Text || fieldType == CompanySettingsType.File || this.isFieldUsingTextValue(fieldName)) {
                if (this.settings.get(fieldGroup).get(fieldName).TextValue != this.prevSettings[i].TextValue)
                    changes.push(this.settings.get(fieldGroup).get(fieldName));
            }
            else {
                if (this.settings.get(fieldGroup).get(fieldName).NumericValue != this.prevSettings[i].NumericValue)
                    changes.push(this.settings.get(fieldGroup).get(fieldName));
            }
        }

        if (this.saveSuccess && changes.length > 0) {
            this.saving = true;
            try {
                this.saveSuccess = await this.CompanySettingsService.SetCompanySettings(changes);
                if (this.saveSuccess) {
                    for (var i = 0; i < this.prevSettings.length; i++) {
                        const fieldName = this.prevSettings[i].FieldName;
                        const fieldGroup = this.prevSettings[i].FieldGroup != null ? this.prevSettings[i].FieldGroup : "General";
                        const fieldType = this.prevSettings[i].FieldType;

                        if (fieldType == CompanySettingsType.Text || fieldType == CompanySettingsType.File  || this.isFieldUsingTextValue(fieldName)) {
                            this.prevSettings[i].TextValue = this.settings.get(fieldGroup).get(fieldName).TextValue;
                        }
                        else {
                            this.prevSettings[i].NumericValue = this.settings.get(fieldGroup).get(fieldName).NumericValue;
                        }
                    }
                    this.hasChange = false;
                }
            }
            catch (e) {
                this.saveSuccess = false;
            }
            finally {
                this.saving = false;
            }
        }
        else
            this.saving = false;

        // Handle errors
        let errList: string[] = [];
        if (!this.saveSuccess)
            errList.push("Company settings failed to save.");
        if (this.ssoErrors.length > 0)
            errList.push(...this.ssoErrors);
        if (errList.length > 0) window['showConfirmDialog'](this.toErrorList(errList), [{ text: "Ok", click: () => jQuery(".errorDialog")['dialog']("close") }],
            "Validation Error", "error");
        else
            this.editing = false;
    }

    public cancel() {
        this.settings = new Map();
        this.settings.set("General", new Map());
        this.settings.set("Customization", new Map());
        this.settings.set("Data Filter", new Map());
        this.settings.set("Gamification", new Map());
        for (var i = 0; i < this.prevSettings.length; i++) {
            if (this.prevSettings[i].FieldGroup == null)
                this.settings.get("General").set(this.prevSettings[i].FieldName, JSON.parse(JSON.stringify(this.prevSettings[i])));
            else
                this.settings.get(this.prevSettings[i].FieldGroup).set(this.prevSettings[i].FieldName, JSON.parse(JSON.stringify(this.prevSettings[i])));
        }

        this.ssoErrors = [];
        this.ssoSettingsValues = cloneDeep(this.prevSSOSettingsValues);
        this.onChange();
        this.editing = false;
    }

    isFieldUsingTextValue(fieldName: string): boolean {
        const FIELDS_USING_TEXT_VALUE = ['Priority_For_User_Identifier'];
        return FIELDS_USING_TEXT_VALUE.includes(fieldName);
    }

    public onChange(): void {
        for (var i = 0; i < this.prevSettings.length; i++) {
            const fieldName = this.prevSettings[i].FieldName;
            const fieldGroup = this.prevSettings[i].FieldGroup != null ? this.prevSettings[i].FieldGroup : "General";
            const fieldType = this.prevSettings[i].FieldType;

            if (fieldType == CompanySettingsType.Text || fieldType == CompanySettingsType.File || this.isFieldUsingTextValue(fieldName)) {
                if (this.settings.get(fieldGroup).get(fieldName).TextValue != this.prevSettings[i].TextValue) {
                    this.hasChange = true;
                    return;
                }
            }
            else {
                if (this.settings.get(fieldGroup).get(fieldName).NumericValue != this.prevSettings[i].NumericValue) {
                    this.hasChange = true;
                    return;
                }
            }
        }

        this.hasChange = false;

        Object.keys(this.ssoSettingsValues).forEach(key => {
            if (this.ssoSettingsValues[key] != this.prevSSOSettingsValues[key])
                this.hasChange = true;
        });
    }

    public getFieldGroup(group: string) {
        return Array.from(this.settings.get(group).values());
    }

    public getTooltip(fieldName: string): string {
        return DEFAULT_COMPANY_TOOLTIP_TEXT[`${fieldName}_T`] || "";
    }

    private toErrorList(messages: string[]) {
        let message: string = "<ul class='errorBulletList'>";
        messages.forEach(m => { message = `${message}<li>${m}</li>` });
        message = `${message}</ul>`;
        return message;
    }
}
